/**
 * Server-side search + label lookups for <SearchableSelect> pickers.
 *
 * Reachability (external audit F2, 2026-10-16): a picker must never make a record unreachable
 * because a fixed window of matches was full. Each search returns a page and says whether more
 * exist; SearchableSelect then offers "Load more". Records that share a name are told apart by the
 * label (city/state, short id) and can be found by their id (full id or 6+ hex prefix).
 *
 * Events are NOT picked here: they use the shared, externally audited EventPicker
 * (components/events/EventPicker.tsx).
 */
import { supabase } from "./supabase";
import { searchContacts } from "./contacts.service";
import { contactPickerCanSearch } from "../utils/contactPickerStatus";
import { sanitizePostgrestSearchTerm } from "../utils/postgrestSearch";
import { parseEventIdPrefix } from "../utils/eventPicker";
import type { SelectOption } from "../components/common/Select";

export const PICKER_PAGE_SIZE = 25;

export interface PickerPage {
  options: SelectOption[];
  /** True when another page exists (offer "Load more"). */
  hasMore: boolean;
  /** Shown under the picker, e.g. when the server cannot page any further. */
  note?: string;
}

/** First 8 hex digits of a uuid, shown so identically named rows can be told apart. */
export const shortId = (id: string) => id.slice(0, 8);

// ---------------------------------------------------------------- organizations

const orgLabel = (o: {
  id: string;
  name: string | null;
  address_city?: string | null;
  address_state?: string | null;
  is_archived?: boolean | null;
}) => {
  const place = [o.address_city, o.address_state]
    .map((s) => (s ?? "").trim())
    .filter(Boolean)
    .join(", ");
  return [
    `${(o.name ?? "").trim() || "(no name)"}${o.is_archived ? " (archived)" : ""}`,
    place,
    `ID ${shortId(o.id)}`,
  ]
    .filter(Boolean)
    .join(" · ");
};

/**
 * One page of non-archived organizations matching the term (name, legal name, city, or an id /
 * 6+ hex id prefix), ordered by name then id. Offset paging is stable here because id is the final
 * sort key; one extra row is read to know whether another page exists.
 */
export const searchOrganizationOptions = async (
  term: string,
  page = 0,
): Promise<PickerPage> => {
  const clean = sanitizePostgrestSearchTerm(term);
  // Accept an id pasted from a label ("ID 1a2b3c4d") as well as a bare id / prefix.
  const idRange = parseEventIdPrefix(term.trim().replace(/^id\s+/i, ""));
  const from = page * PICKER_PAGE_SIZE;
  let query = supabase
    .from("organizations")
    .select("id, name, address_city, address_state")
    .eq("is_archived", false)
    .order("name", { ascending: true })
    .order("id", { ascending: true })
    .range(from, from + PICKER_PAGE_SIZE); // PAGE_SIZE + 1 rows
  const clauses: string[] = [];
  if (clean) {
    clauses.push(
      `name.ilike.*${clean}*`,
      `legal_name.ilike.*${clean}*`,
      `address_city.ilike.*${clean}*`,
      `address_state.ilike.*${clean}*`,
    );
  }
  if (idRange) {
    clauses.push(`and(id.gte.${idRange.lo},id.lte.${idRange.hi})`);
  }
  if (clauses.length) {
    query = query.or(clauses.join(","));
  }
  const { data, error } = await query;
  if (error) {
    throw error;
  }
  const rows = data ?? [];
  return {
    options: rows
      .slice(0, PICKER_PAGE_SIZE)
      .map((o) => ({ value: o.id, label: orgLabel(o) })),
    hasMore: rows.length > PICKER_PAGE_SIZE,
  };
};

export const organizationLabel = async (id: string): Promise<string | null> => {
  const { data, error } = await supabase
    .from("organizations")
    .select("id, name, address_city, address_state, is_archived")
    .eq("id", id)
    .maybeSingle();
  if (error) {
    throw error;
  }
  return data ? orgLabel(data) : null;
};

// ---------------------------------------------------------------- contacts

/** The shared contact picker RPC returns at most this many rows and has no offset. */
export const CONTACT_PICKER_MAX = 50;

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Contacts go through the shared search_contacts_picker RPC (internal staff only, indexed, excludes
 * merged contacts; matches First Last either order, e-mail, phone in any format, member #). It has
 * no offset, so it cannot page: when it returns its maximum, the picker says so and asks for a
 * narrower term. Every contact stays reachable because e-mail, phone, member # and the contact id
 * are unique handles; the label shows e-mail, member #, phone and a short id so same-named people differ.
 */
export const searchContactOptions = async (
  term: string,
): Promise<PickerPage> => {
  const t = term.trim();
  if (UUID_RE.test(t)) {
    // Same rule as the picker RPC: a contact merged into another is not offered.
    const { data, error } = await supabase
      .from("contacts")
      .select("id, first_name, last_name, email, membership_id, phone_number")
      .eq("id", t.toLowerCase())
      .is("merged_into", null)
      .maybeSingle();
    if (error) {
      throw error;
    }
    return {
      options: data ? [{ value: data.id, label: contactLabelOf(data) }] : [],
      hasMore: false,
    };
  }
  if (!contactPickerCanSearch(t)) {
    return { options: [], hasMore: false };
  }
  const rows = await searchContacts(t, CONTACT_PICKER_MAX);
  return {
    options: rows.map((c) => ({ value: c.id, label: contactLabelOf(c) })),
    hasMore: false,
    note:
      rows.length >= CONTACT_PICKER_MAX
        ? `Showing the first ${CONTACT_PICKER_MAX} matches. Search by e-mail, phone, member # or contact ID to find a specific person.`
        : undefined,
  };
};

/** Current value of a record being edited (merged contacts included, so an old link still shows). */
export const contactLabel = async (id: string): Promise<string | null> => {
  const { data, error } = await supabase
    .from("contacts")
    .select("id, first_name, last_name, email, membership_id, phone_number")
    .eq("id", id)
    .maybeSingle();
  if (error) {
    throw error;
  }
  return data ? contactLabelOf(data) : null;
};

interface ContactLabelFields {
  id: string;
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
  membership_id?: string | null;
  phone_number?: string | null;
}

/**
 * "Name · e-mail · #member · phone · ID 1a2b3c4d". The short id is always shown: thousands of
 * contacts have neither e-mail nor member #, so same-named people must still differ.
 */
export const contactLabelOf = (c: ContactLabelFields): string => {
  const name = [c.first_name, c.last_name].filter(Boolean).join(" ").trim();
  return [
    name || "(no name)",
    c.email || null,
    c.membership_id ? `#${c.membership_id}` : null,
    c.phone_number || null,
    `ID ${shortId(c.id)}`,
  ]
    .filter(Boolean)
    .join(" · ");
};
